<template>
  <div class="pre-tx-ack p-2">
    <b-form-row class="mb-2">
      <b-col>
        <div
          class="alert mb-0 py-1 px-2 small-text"
          :class="disease.danger ? 'alert-warning' : 'alert-light'"
        >
          <b>โรคประจำตัว:</b> {{ disease.text }}
        </div>
      </b-col>
      <b-col>
        <div
          class="alert mb-0 py-1 px-2 small-text"
          :class="allergy.danger ? 'alert-danger' : 'alert-light'"
        >
          <b>แพ้ยา:</b> {{ allergy.text }}
        </div>
      </b-col>
    </b-form-row>

    <div class="small-text mb-2">
      <b>ยาที่ใช้:</b> {{ medication.text }}
      <span
        v-if="row.patient.antithrombotic"
        class="badge tag-antithrombotic ml-1"
        >ต้านเกล็ดเลือด</span
      >
    </div>
    <PatientTagBadges :tag-ids="row.patient.tagIds" with-desc class="mb-2" />

    <div v-if="!reviewed" class="health-review-block alert alert-warning p-2 mb-2">
      <div class="small-text mb-1">
        ยังไม่ได้ทบทวนประวัติสุขภาพวันนี้ · อัปเดตล่าสุด {{ lastUpdatedText }} ·
        ถามคนไข้ก่อนเริ่มรักษา
      </div>
      <HealthReviewCard v-model="review" compact :patient="row.patient" />
      <div v-if="review && review.result === 'CHANGED'" class="mt-2 text-right">
        <b-button
          size="sm"
          variant="primary"
          :disabled="isSavingReview"
          @click="saveReview(review)"
        >
          บันทึกประวัติสุขภาพ
        </b-button>
      </div>
    </div>

    <VitalSignsSummary
      v-if="row.vitalSigns.length"
      :readings="row.vitalSigns"
      class="mb-2"
    />
    <div v-else-if="!showVitalsForm" class="d-flex align-items-center mb-2">
      <span class="description text-muted mr-2"
        >ยังไม่ได้วัด vital signs (ไม่บังคับ)</span
      >
      <b-button
        size="sm"
        variant="outline-info"
        class="py-0"
        @click="showVitalsForm = true"
      >
        + วัด
      </b-button>
    </div>
    <div v-else class="mb-2">
      <VitalSignsForm
        v-model="draft"
        :last-measurements="lastMeasurements"
        :id-prefix="`ack-${row.id}`"
      />
      <div class="d-flex justify-content-between align-items-center mt-1">
        <span class="description text-muted"
          >ยังไม่ได้วัด (ไม่บังคับ) · ผู้ช่วยกรอกได้ที่นี่</span
        >
        <b-button
          size="sm"
          variant="outline-info"
          :disabled="isSaving"
          @click="saveVitals"
        >
          บันทึก vital signs
        </b-button>
      </div>
    </div>

    <div class="d-flex justify-content-end">
      <b-button
        variant="primary"
        :disabled="isAcking || !reviewed"
        @click="acknowledge"
      >
        รับทราบ และเริ่มรักษา
      </b-button>
    </div>
  </div>
</template>

<script>
import { eventBus } from "@/main";
import VitalSignsForm from "@/components/aaci/VitalSignsForm";
import VitalSignsSummary from "@/components/aaci/VitalSignsSummary";
import HealthReviewCard from "@/components/aaci/HealthReviewCard";
import PatientTagBadges from "@/components/aaci/PatientTagBadges";
import { isHealthReviewed } from "@/utils/healthReview";
import { healthDisplay } from "@/utils/healthText";
import { emptyReading, pendingReadings } from "@/utils/vitalSigns";
import moment from "moment";

export default {
  name: "PreTreatmentAckCard",
  components: {
    VitalSignsForm,
    VitalSignsSummary,
    HealthReviewCard,
    PatientTagBadges,
  },
  props: {
    row: { type: Object, required: true },
  },
  data() {
    return {
      draft: [emptyReading()],
      lastMeasurements: null,
      isSaving: false,
      isAcking: false,
      showVitalsForm: false,
      review: null,
      isSavingReview: false,
    };
  },
  watch: {
    review(value) {
      // CHANGED is saved only via the explicit button, so the doctor can edit.
      if (value && value.result === "NO_CHANGE") this.saveReview(value);
    },
  },
  computed: {
    disease() {
      return healthDisplay(this.row.patient.congenitalDisease);
    },
    allergy() {
      return healthDisplay(this.row.patient.allergic);
    },
    medication() {
      return healthDisplay(this.row.patient.medicationHistory);
    },
    reviewed() {
      return isHealthReviewed(this.row);
    },
    lastUpdatedText() {
      const dt = this.row.patient.healthUpdatedDt;
      return dt ? moment(dt).format("D MMM YY") : "-";
    },
    routeParams() {
      return {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      };
    },
  },
  mounted() {
    if (!this.row.vitalSigns.length && this.row.patient.id) {
      this.$store
        .dispatch("moduleAppointment/fetchLatestBodyMeasurements", {
          ...this.routeParams,
          patientId: this.row.patient.id,
        })
        .then(res => {
          this.lastMeasurements = res.data;
        })
        .catch(() => {
          this.lastMeasurements = null;
        });
    }
  },
  methods: {
    toast(message, variant = "warning") {
      eventBus.$emit("alertToast", { message, variant });
    },
    async saveReview(review) {
      if (this.isSavingReview) return;
      this.isSavingReview = true;
      try {
        await this.$store.dispatch("moduleAppointment/saveHealthReview", {
          ...this.routeParams,
          appointmentId: this.row.id,
          review,
        });
      } catch (err) {
        // Keep a CHANGED form/value so the user can retry; NO_CHANGE is re-pickable.
        if (review.result === "NO_CHANGE") this.review = null;
        this.toast(
          err.response?.data?.errorMessage || "บันทึกการทบทวนสุขภาพไม่สำเร็จ",
          "danger"
        );
      } finally {
        this.isSavingReview = false;
      }
    },
    async saveVitals() {
      const { readings, error } = pendingReadings(this.draft);
      if (!readings.length) {
        this.toast("กรุณากรอก vital signs อย่างน้อย 1 ค่า");
        return;
      }
      if (error) {
        this.toast(error);
        return;
      }
      await this.persistVitals(readings);
    },
    /** @returns {Promise<boolean>} whether the readings were saved */
    async persistVitals(readings) {
      this.isSaving = true;
      try {
        await this.$store.dispatch("moduleAppointment/addVitalSigns", {
          ...this.routeParams,
          appointmentId: this.row.id,
          readings,
          source: "DASHBOARD",
        });
        this.draft = [emptyReading()];
        return true;
      } catch (err) {
        this.toast(
          err.response?.data?.errorMessage || "บันทึก vital signs ไม่สำเร็จ",
          "danger"
        );
        return false;
      } finally {
        this.isSaving = false;
      }
    },
    async acknowledge() {
      // Vitals typed in but not saved yet are saved first, so they aren't lost.
      const { readings, error } = pendingReadings(this.draft);
      if (error) {
        this.toast(error);
        return;
      }
      if (readings.length && !(await this.persistVitals(readings))) return;
      this.isAcking = true;
      try {
        await this.$store.dispatch("moduleAppointment/acknowledgeAppointment", {
          ...this.routeParams,
          id: this.row.id,
        });
        this.$emit("acknowledged", this.row.id);
      } catch (err) {
        if (err.response?.data?.errorMessage === "APPT-NOT-CHECKED-IN") {
          this.toast("คนไข้รายนี้ไม่ได้อยู่ในคิวรอตรวจแล้ว");
          this.$emit("refresh");
        } else if (
          err.response?.data?.errorMessage === "HEALTH-REVIEW-REQUIRED"
        ) {
          this.toast("กรุณาทบทวนประวัติสุขภาพก่อน");
          const block = this.$el.querySelector(".health-review-block");
          if (block && block.scrollIntoView) block.scrollIntoView();
        } else {
          this.toast(
            err.response?.data?.errorMessage || "รับทราบไม่สำเร็จ",
            "danger"
          );
        }
      } finally {
        this.isAcking = false;
      }
    },
  },
};
</script>
